import { useEffect, useState } from "react";
import locales from "../locales.json";
import useI18n from "../hooks/useI18n";

const Icon = {
    shareIOS: (cls = "h-3.5 w-3.5") => (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <rect x="5" y="7" width="14" height="14" rx="3" />
            <path d="M12 3v10M12 3l4 4M12 3 8 7" />
        </svg>
    ),
    addHomeIOS: (cls = "h-3.5 w-3.5") => (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <rect x="5" y="5" width="14" height="14" rx="3" />
            <path d="M12 8v8M8 12h8" />
        </svg>
    ),
    installAndroid: (cls = "h-3.5 w-3.5") => (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <path d="M12 3v10" />
            <path d="M8 9l4 4 4-4" />
            <rect x="4" y="17" width="16" height="4" rx="1.5" />
        </svg>
    ),
    addHomeAndroid: (cls = "h-3.5 w-3.5") => (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <path d="M3 11l9-7 9 7" />
            <path d="M5 10v9a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-9" />
            <path d="M12 12v6M9 15h6" />
        </svg>
    ),
    menu: (cls = "h-5 w-5") => (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <path d="M3 6h18M3 12h18M3 18h18" />
        </svg>
    ),
    close: (cls = "h-5 w-5") => (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <path d="M6 6l12 12M18 6l-12 12" />
        </svg>
    ),
    arrowRight: (cls = "h-5 w-5") => (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <path d="M5 12h14M13 5l7 7-7 7" />
        </svg>
    ),
    camera: (cls = "h-4 w-4") => (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <rect x="3" y="6" width="18" height="12" rx="2" />
            <circle cx="12" cy="12" r="3.5" />
            <path d="M8 6l1.5-2h5L16 6" />
        </svg>
    ),
    chevron: (cls = "h-5 w-5") => (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <path d="M6 9l6 6 6-6" />
        </svg>
    ),
    mapPin: (cls = "h-5 w-5") => (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <path d="M12 22s7-5.33 7-12A7 7 0 0 0 5 10c0 6.67 7 12 7 12z" />
            <circle cx="12" cy="10" r="3" />
        </svg>
    ),
    shield: (cls = "h-5 w-5") => (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <path d="M12 3l7 4v5c0 5-3.5 8-7 9-3.5-1-7-4-7-9V7l7-4z" />
        </svg>
    ),
    clock: (cls = "h-5 w-5") => (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v5l3 3" />
        </svg>
    ),
    bookmark: (cls = "h-5 w-5") => (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <path d="M6 4h12v16l-6-3-6 3V4z" />
        </svg>
    ),
    offline: (cls = "h-5 w-5") => (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <path d="M4 4l16 16" />
            <path d="M2 12a10 10 0 0 1 16.9-7.9M21.8 16A10 10 0 0 1 6.9 22" />
        </svg>
    ),
    privacy: (cls = "h-5 w-5") => (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <path d="M12 3l8 4v5a8 8 0 1 1-16 0V7l8-4z" />
            <path d="M9 13a3 3 0 1 0 6 0v-1a3 3 0 1 0-6 0v1z" />
        </svg>
    )
};

function Logo() {
    const { t } = useI18n(locales);
    return (
        <div className="flex items-center gap-2">
            <img src="/logo512.png" alt={`${t("app.name")} logo`} className="h-8 w-8 rounded-xl object-cover" />
            <span className="text-lg font-semibold tracking-tight">{t("app.name")}</span>
        </div>
    );
}

function NavLink({ href, icon, children, onClick }) {
    return (
        <a href={href} onClick={onClick} className="inline-flex items-center gap-2 hover:text-white">
            {icon}
            <span>{children}</span>
        </a>
    );
}

function Section({ id, title, children, className = "" }) {
    return (
        <section id={id} className={`scroll-mt-24 ${className}`}>
            <h2 className="text-xl sm:text-2xl font-semibold flex items-center gap-2">
                {Icon.arrowRight("h-5 w-5 text-emerald-400")} {title}
            </h2>
            <div className="mt-3 text-white/80">{children}</div>
        </section>
    );
}

function Feature({ icon, title, desc }) {
    return (
        <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
            <div className="flex items-center gap-2">
        <span className="inline-grid h-8 w-8 place-items-center rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-300">
          {icon}
        </span>
                <h3 className="font-medium">{title}</h3>
            </div>
            <p className="mt-2 text-sm text-white/70">{desc}</p>
        </div>
    );
}

function Step({ n, title, desc }) {
    return (
        <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
            <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-full bg-white/10 grid place-items-center text-sm">{n}</div>
                <h3 className="font-medium">{title}</h3>
            </div>
            <p className="mt-2 text-sm text-white/70">{desc}</p>
        </div>
    );
}

function ScreenshotCard({ image }) {
    const src = image?.startsWith("/") ? image : `/${image}`;
    return (
        <div className="relative w-full aspect-[9/16]" style={{pointerEvents: "none"}}>
            <div className="absolute inset-0 rounded-[2.2rem] bg-slate-900 shadow-[0_20px_60px_rgba(0,0,0,0.55)] ring-1 ring-black/30">
                <div className="absolute inset-0 rounded-[2.2rem] bg-gradient-to-br from-slate-800 to-slate-700" />
                <div className="absolute inset-[6px] rounded-[2rem] bg-slate-950 ring-1 ring-white/10" />

                <div className="absolute left-[-3px] top-24 h-16 w-1.5 rounded-l-md bg-slate-500/70" />
                <div className="absolute left-[-3px] top-48 h-10 w-1.5 rounded-l-md bg-slate-500/70" />
                <div className="absolute right-[-3px] top-36 h-14 w-1.5 rounded-r-md bg-slate-500/70" />

                <div className="absolute inset-[10px] rounded-[1.8rem] overflow-hidden bg-black">
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-28 h-3 rounded-b-2xl bg-black border-x border-b border-white/10" />
                    <div className="absolute top-1 left-1/2 -translate-x-1/2 w-12 h-0.5 rounded-full bg-white/20" />
                    <div className="absolute top-1 left-[calc(50%+40px)] w-1.5 h-1.5 rounded-full bg-white/20" />
                    <div className="pointer-events-none absolute -top-8 -left-8 w-2/3 h-1/2 rotate-[-12deg] bg-gradient-to-b from-white/10 to-transparent" />
                    <img src={src} alt="" className="w-full h-full object-cover" />
                </div>
            </div>
        </div>
    );
}

function FAQItem({ q, a }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="rounded-xl border border-white/10 bg-white/5">
            <button onClick={() => setOpen(!open)} className="w-full text-left px-4 py-3 flex items-center justify-between">
                <span className="font-medium">{q}</span>
                <span className={`transition-transform ${open ? "rotate-180" : ""}`}>
          {Icon.chevron("h-5 w-5 text-white/60")}
        </span>
            </button>
            {open && <div className="px-4 pb-4 text-sm text-white/70">{a}</div>}
        </div>
    );
}

export default function AboutInstall() {
    const { t } = useI18n(locales);
    const [menuOpen, setMenuOpen] = useState(false);

    useEffect(() => {
        try {
            const prev = document.documentElement.style.scrollBehavior;
            document.documentElement.style.scrollBehavior = "smooth";
            return () => {
                document.documentElement.style.scrollBehavior = prev || "";
            };
        } catch {}
    }, []);

    return (
        <div className="min-h-screen bg-slate-950 text-white overflow-x-hidden">
            <header className="sticky top-0 z-30 border-b border-white/10 bg-slate-950/60 backdrop-blur-md supports-[backdrop-filter]:bg-slate-950/40">
                <div className="mx-auto max-w-6xl px-4 py-3 flex items-center justify-between">
                    <Logo />
                    <nav className="hidden sm:flex items-center gap-6 text-sm text-white/80">
                        <NavLink href="#o-projektu" icon={Icon.arrowRight("h-4 w-4 text-emerald-400")}>{t("nav.about")}</NavLink>
                        <NavLink href="#jak-funguje" icon={Icon.arrowRight("h-4 w-4 text-emerald-400")}>{t("nav.how")}</NavLink>
                        <NavLink href="#funkce" icon={Icon.arrowRight("h-4 w-4 text-emerald-400")}>{t("nav.features")}</NavLink>
                        <NavLink href="#faq" icon={Icon.arrowRight("h-4 w-4 text-emerald-400")}>{t("nav.faq")}</NavLink>
                    </nav>
                    <div className="hidden sm:block">
                        <a href="#instalace" className="px-3 py-1.5 rounded-lg bg-emerald-500/90 hover:bg-emerald-500 text-sm inline-flex items-center gap-2">
                            {Icon.arrowRight("h-4 w-4")} {t("nav.install")}
                        </a>
                    </div>
                    <button type="button" aria-label="Menu" onClick={() => setMenuOpen(true)} className="sm:hidden inline-flex items-center justify-center rounded-lg px-3 py-2 border border-white/15 bg-white/5 hover:bg-white/10">
                        {Icon.menu("h-5 w-5")}
                    </button>
                </div>
            </header>

            {menuOpen && (
                <div className="sm:hidden fixed inset-0 z-40 overflow-hidden" aria-modal="true" role="dialog">
                    <div onClick={() => setMenuOpen(false)} className="absolute inset-0 bg-black/50" />
                    <aside className="absolute top-0 right-0 h-full w-72 transform translate-x-0 bg-slate-950/90 backdrop-blur-md border-l border-white/10">
                        <div className="px-4 py-3 flex items-center justify-between border-b border-white/10">
                            <Logo />
                            <button type="button" aria-label="Zavřít menu" onClick={() => setMenuOpen(false)} className="inline-flex items-center justify-center rounded-lg px-3 py-2 border border-white/15 bg-white/5 hover:bg-white/10">
                                {Icon.close("h-5 w-5")}
                            </button>
                        </div>
                        <nav className="p-4 grid gap-2 text-white/90">
                            <NavLink href="#o-projektu" onClick={() => setMenuOpen(false)} icon={Icon.arrowRight("h-4 w-4 text-emerald-400")}>{t("nav.about")}</NavLink>
                            <NavLink href="#jak-funguje" onClick={() => setMenuOpen(false)} icon={Icon.arrowRight("h-4 w-4 text-emerald-400")}>{t("nav.how")}</NavLink>
                            <NavLink href="#funkce" onClick={() => setMenuOpen(false)} icon={Icon.arrowRight("h-4 w-4 text-emerald-400")}>{t("nav.features")}</NavLink>
                            <NavLink href="#faq" onClick={() => setMenuOpen(false)} icon={Icon.arrowRight("h-4 w-4 text-emerald-400")}>{t("nav.faq")}</NavLink>
                            <a onClick={() => setMenuOpen(false)} href="#instalace" className="mt-2 rounded-lg px-3 py-2 bg-emerald-500/90 hover:bg-emerald-500 text-center inline-flex items-center justify-center gap-2">
                                {Icon.arrowRight("h-4 w-4")} {t("nav.install")}
                            </a>
                        </nav>
                    </aside>
                </div>
            )}

            <div className="relative">
                <div className="absolute inset-0 pointer-events-none overflow-hidden">
                    <div className="absolute -top-24 left-1/2 -translate-x-1/2 h-72 w-[60rem] max-w-[120vw] bg-emerald-500/10 blur-3xl rounded-full" />
                </div>
                <section className="mx-auto max-w-6xl px-4 py-14 sm:py-20">
                    <div className="grid md:grid-cols-[70%_30%] gap-8 items-center">
                        <div>
                            <h1 className="text-3xl sm:text-5xl font-semibold leading-tight">
                                <span dangerouslySetInnerHTML={{ __html: t("hero.title_html") }} />
                            </h1>
                            <p className="mt-4 text-white/70">{t("hero.desc")}</p>
                            <div className="mt-6 flex flex-wrap items-center gap-3">
                                <a href="#instalace" className="px-4 py-2 rounded-xl bg-emerald-500/90 hover:bg-emerald-500 inline-flex items-center gap-2">
                                    {Icon.arrowRight("h-5 w-5")} {t("hero.cta_install")}
                                </a>
                                <a href="#o-projektu" className="px-4 py-2 rounded-xl border border-white/15 hover:bg-white/5 inline-flex items-center gap-2">
                                    {Icon.arrowRight("h-5 w-5")} {t("hero.cta_learn")}
                                </a>
                            </div>
                        </div>

                        <div className="flex justify-center md:justify-end">
                            <div className="w-[180px] sm:w-[220px]">
                                <ScreenshotCard image="screens/preview.png" />
                            </div>
                        </div>
                    </div>
                </section>
            </div>

            <main className="mx-auto max-w-6xl px-4 pb-16">
                <Section id="o-projektu" title={t("sections.about_title")} className="mt-10">
                    <p>{t("sections.about_desc")}</p>
                </Section>

                <Section id="jak-funguje" title={t("sections.how_title")} className="mt-12">
                    <div className="grid sm:grid-cols-3 gap-4">
                        <Step n="1" title={t("steps.1_title")} desc={t("steps.1_desc")} />
                        <Step n="2" title={t("steps.2_title")} desc={t("steps.2_desc")} />
                        <Step n="3" title={t("steps.3_title")} desc={t("steps.3_desc")} />
                    </div>
                </Section>

                <Section id="funkce" title={t("sections.features_title")} className="mt-12">
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        <Feature icon={Icon.mapPin()} title={t("features.f1_title")} desc={t("features.f1_desc")} />
                        <Feature icon={Icon.shield()} title={t("features.f2_title")} desc={t("features.f2_desc")} />
                        <Feature icon={Icon.clock()} title={t("features.f3_title")} desc={t("features.f3_desc")} />
                        <Feature icon={Icon.bookmark()} title={t("features.f4_title")} desc={t("features.f4_desc")} />
                        <Feature icon={Icon.offline()} title={t("features.f5_title")} desc={t("features.f5_desc")} />
                        <Feature icon={Icon.privacy()} title={t("features.f6_title")} desc={t("features.f6_desc")} />
                    </div>
                </Section>

                <Section id="instalace" title={t("sections.install_title")} className="mt-12">
                    <div className="grid md:grid-cols-2 gap-4">
                        <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                            <h3 className="font-medium">iOS (Safari)</h3>
                            <ol className="mt-2 space-y-1 text-sm text-white/80 list-decimal list-inside">
                                <li>
                                    {t("install.ios_1")}{" "}
                                    <span className="px-1 rounded bg-white/10 inline-flex items-center gap-1">
                    {Icon.shareIOS()} {t("install.ios_share")}
                  </span>.
                                </li>
                                <li>
                                    {t("install.ios_2")}{" "}
                                    <span className="px-1 rounded bg-white/10 inline-flex items-center gap-1">
                    {Icon.addHomeIOS()} {t("install.ios_add")}
                  </span>.
                                </li>
                                <li>{t("install.ios_3")}</li>
                            </ol>
                        </div>
                        <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                            <h3 className="font-medium">Android (Chrome)</h3>
                            <ol className="mt-2 space-y-1 text-sm text-white/80 list-decimal list-inside">
                                <li>{t("install.android_1")} <span className="px-1 rounded bg-white/10 inline-flex items-center gap-1">⋮</span>.</li>
                                <li>
                                    {t("install.android_2")}{" "}
                                    <span className="px-1 rounded bg-white/10 inline-flex items-center gap-1">
                    {Icon.installAndroid()} {t("install.android_install")}
                  </span>
                                    {t("install.android_or")}
                                    <span className="px-1 rounded bg-white/10 inline-flex items-center gap-1">
                    {Icon.addHomeAndroid()} {t("install.android_add")}
                  </span>.
                                </li>
                                <li>{t("install.android_3")}</li>
                            </ol>
                        </div>
                    </div>
                </Section>

                <Section id="faq" title={t("sections.faq_title")} className="mt-12">
                    <div className="grid gap-3">
                        <FAQItem q={t("faq.q1")} a={t("faq.a1")} />
                        <FAQItem q={t("faq.q2")} a={t("faq.a2")} />
                        <FAQItem q={t("faq.q3")} a={t("faq.a3")} />
                        <FAQItem q={t("faq.q4")} a={t("faq.a4")} />
                        <FAQItem q={t("faq.q5")} a={t("faq.a5")} />
                        <FAQItem q={t("faq.q6")} a={t("faq.a6")} />
                    </div>
                </Section>
            </main>

            <footer className="border-t border-white/10">
                <div className="mx-auto max-w-6xl px-4 py-10 text-sm text-white/60">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <Logo />
                        <div className="flex gap-6">
                            <NavLink href="#o-projektu" icon={Icon.arrowRight("h-4 w-4 text-emerald-400")}>{t("nav.about")}</NavLink>
                            <NavLink href="#instalace" icon={Icon.arrowRight("h-4 w-4 text-emerald-400")}>{t("nav.install")}</NavLink>
                            <NavLink href="#" icon={Icon.arrowRight("h-4 w-4 text-emerald-400")}>{t("footer.contact")}</NavLink>
                            <NavLink href="#" icon={Icon.arrowRight("h-4 w-4 text-emerald-400")}>{t("footer.privacy")}</NavLink>
                        </div>
                    </div>
                    <p className="mt-6">
                        {t("footer.copyright_prefix")} {new Date().getFullYear()} {t("footer.copyright_suffix")} {t("app.copyright")}
                    </p>
                </div>
            </footer>
        </div>
    );
}
